<template>
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.videoCategoryList.detailModal.new.buttonText')"
      @click="categoryModalOpen = true"
    />
    <export
      url="/video_categories/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.videoCategories')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <video-category-modal
    v-model:open="categoryModalOpen"
    v-model:category="editedCategory"
    @saved="refetch"
  />
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
  >
    <template #file="{ item }">
      <file v-slot="{ url, type }" :src="item.file?.contentUrl" class="py-4">
        <box :aspect="0.6" class="medium">
          <medium
            v-if="url"
            class="w-auto h-full"
            :url="url"
            :type="type"
          />
        </box>
      </file>
    </template>
  </data-table>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  ref, reactive, computed, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';
import Medium from '@utils/Medium.vue';
import File from '@utils/File.vue';

import type { Filter } from '@utils/filter/Filter.vue';
import Box from '@utils/Box.vue';
import { useDelete } from '@hooks/api';
import Export from '@utils/toolbar/panes/Export.vue';
import { useTableFields } from '@hooks/table-configuration';
import type { HydraCollection, VideoCategory } from '@/entities/Entities';
import VideoCategoryModal from './VideoCategoryModal.vue';
import { translationFor } from '@/utils/api';
import { useQueryRef } from '@/hooks/url';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});
const totalFilter = computed(() => ({ ...filter.value }));

const categoryModalOpen = ref(false);
const editedCategory = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteVideoCategory, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.videoCategoryList.actions.edit'),
    action: (item: VideoCategory) => {
      categoryModalOpen.value = true;
      editedCategory.value = item;
    },
  },
  {
    title: t('pages.videoCategoryList.actions.delete'),
    action: async (item: VideoCategory) => {
      if (await deleteVideoCategory(`/video_categories/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

const translate = translationFor();

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.videoCategoryList.fieldGroups.category'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'orderNumber',
    title: t('videoCategory.orderNumber'),
    group: t('pages.videoCategoryList.filterGroups.category'),
    visible: true,
  },
  {
    key: 'target',
    title: t('videoCategory.target.DEFAULT'),
    group: t('pages.videoCategoryList.fieldGroups.category'),
    formatter: (v: string) => t(`videoCategory.target.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.videoCategoryList.fieldGroups.category'),
      options: {
        options: enumOptionsProvider('/enums/VideoCategoryTarget', (v) => ({
          label: t(`videoCategory.target.${v}`),
          value: v,
        })),
        label: t('videoCategory.target.DEFAULT'),
      },
    },
  },
  {
    key: 'translations.title.en',
    title: t('videoCategory.title'),
    group: t('pages.videoCategoryList.fieldGroups.category'),
    computed: (item: VideoCategory) => translate(item, 'title') ?? '',
    visible: true,
    filter: {
      key: 'translations.title',
      type: 'text',
      group: t('pages.videoCategoryList.filterGroups.category'),
      options: {
        label: t('videoCategory.title'),
      },
    },
  },
  {
    key: 'translations.description.en',
    title: t('videoCategory.description'),
    group: t('pages.videoCategoryList.filterGroups.category'),
    computed: (item: VideoCategory) => translate(item, 'description') ?? '',
    visible: true,
    filter: {
      key: 'translations.description',
      type: 'text',
      group: t('pages.videoCategoryList.filterGroups.category'),
      options: {
        label: t('videoCategory.description'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<VideoCategory>>('/video_categories', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

